/* eslint-disable max-len */
import {
  Component,
  h,
  Prop,
  Event,
  EventEmitter,
  Watch,
  State,
  Element,
  Method,
  Listen,
} from '@stencil/core';
import IMask, { InputMask } from 'imask';
import { versionComponent } from '../../global/inject-dependencies';

export interface UiInputChangeEvent {
  value: string;
  changedBy?: string;
  checked?: boolean;
}

@Component({
  tag: 'ui-input',
  styleUrl: 'ui-input.scss',
  shadow: false,
})
export class UiInput {
  @Element() el: HTMLElement;

  @Prop() label?: string;

  /**
   * Specifies the type `<input>` element to display. The default type is *text*.
   */
  @Prop({ reflect: true }) type?: HTMLInputElement['type'] = 'text';

  /**
   * Specifies a short hint that describes the expected value of an `<input>` element.
   */
  @Prop() placeholder?: string;

  /**
   * Set to true if the user's input is invalid and wrong.
   */
  @Prop() error?: boolean;

  /**
   * Set to true if the input's component input is able to be cleared.
   */
  @Prop() clearable?: boolean;

  /**
   * Message that appears below the input element, informing the user of something.
   */
  @Prop() message?: string;

  /**
   * Specifies which icon appears (usually in a search input, at the end of the bar)
   */
  @Prop() icon?: string;

  @Prop() required?: boolean;

  /**
   * Set to true if the `<input>` component is disabled.
   */
  @Prop() disabled?: boolean;

  /**
   * Set to true if the user's input is a success.
   */
  @Prop() success?: boolean;

  /**
   * Specifies the name the `<input>` component.
   */
  @Prop() name?: string;

  /**
   * Value inserted into the `<input>` component by the user.
   */
  @Prop({ mutable: true }) value?: string;

  /**
   * Initially sets the keyboard focus to the input element.
   */
  @Prop() focus? = false;

  /**
   * Gives the user a clue for what type of information or action is required of them.
   */
  @Prop() hint?: string;

  /**
   * Checks whether the component has been checked. It is used for checkboxes and radio inputs.
   */
  @Prop({ mutable: true }) checked?: boolean;

  @Prop() uppercase?: boolean;

  @Prop() lowercase?: boolean;

  @Prop() mask?: string;

  /**
   * Transforms an input type="checkbox" into a switch component
   */
  @Prop() switch = false;

  // radio button specific props
  /**
   * Text that appears under input label
   */
  @Prop() description?: string;

  /**
   * Defines the radiobutton style - stylized variants are applying different css style
   */
  @Prop({ reflect: true }) variant?: 'default' | 'stylized' | 'stylized-big' =
    'default';

  // type number props
  /**
   * Maximal value.
   */
  @Prop() max?: number;

  /**
   * Minimal value.
   */
  @Prop() min?: number;

  /**
   * Value of step.
   */
  @Prop() stepSize? = 1;

  /**
   * Should stepControls be visible or not.
   */
  @Prop() stepControls? = false;

  plusDisabled = false;

  minusDisabled = false;

  /**
   * Should autocorrect be active or not.
   */
  @Prop() autocorrect? = false;

  /**
   * How is autocorrect behaving when value is not in min-max range:
   * changing value or focusing input field.
   */
  @Prop() autocorrectType?: 'change' | 'defocus' = 'change';

  /**
   * How is autocorrect behaving when value is not in min-max range:
   * changing value or focusing input field.
   */
  @Prop() focusOnIconClick = false;

  changeValue = false;

  focusValue = false;

  /**
   * Unit for numbers.
   */
  @Prop() unit?: string;

  /**
   * Digit for decimal places
   */
  @Prop() scale?: number = 0;

  radix = ',';

  hasFractionalDelimiter = false;

  /**
   * Is there thousands separator or not
   */
  @Prop() thousandsSeparator = false;

  thousandsSeparatorCharacter = '.';

  /**
   * If we want to cover number input with inputMask then
   * input type is changed to text, and mask gets special maskType
   */
  typeNumberCoveredByMask = false;

  /**
   * In case label is not just simple text, but contains <a> element for example.
   * In this case label will be added trough innerHTML
   */
  @Prop() labelIsHtml = false;

  @State() inputError = false;

  @State() inputId: HTMLElement['id'];

  /**
   * Is triggered when the user changes something on the `<input>` element.
   */
  @Event({ eventName: 'uiInputChange' })
  changeEvent: EventEmitter<UiInputChangeEvent>;

  /**
   * Is triggered when the user focus on the `<input>` element.
   */
  @Event({ eventName: 'uiInputFocus' }) focusEvent: EventEmitter;

  /**
   * Is triggered when the user loses focus on the `<input>` element.
   */
  @Event({ eventName: 'uiInputBlur' }) blurEvent: EventEmitter;

  /**
   * Emitted when input value is ''.
   * Serves as a replacement for changeEvent.emit({ changedBy: 'clearButton' });
   */
  @Event({ eventName: 'uiInputClear' }) clearEvent: EventEmitter;

  /**
   * Is triggered when the user clicks on the input's icon.
   * todo: refactor event name to 'uiInputIconClick'
   */
  @Event({ eventName: 'inputIconClicked' }) iconClickEvent: EventEmitter;

  /**
   * Is triggered when the "Enter"-key is pressed
   */
  @Event({ eventName: 'uiInputEnterKeyDown', composed: true })
  enterKeyDown: EventEmitter;

  /**
   * Is triggered when component renders
   */
  @Event({ eventName: 'uiInputDidRender' }) didRender: EventEmitter;

  input: HTMLInputElement;

  maskInstance: InputMask<any>;

  isCheckbox(): boolean {
    return this.type === 'checkbox';
  }

  isRadio(): boolean {
    return this.type === 'radio';
  }

  isCheckboxOrRadio(): boolean {
    return this.isRadio() || this.isCheckbox();
  }

  @Listen('keydown')
  handleKeyDown(ev: KeyboardEvent) {
    if (ev.key === 'Enter') {
      this.enterKeyDown.emit({ value: this.value });
    }
  }

  onKeyUp() {
    this.onChange();
  }

  onFocus() {
    this.focusEvent.emit();
  }

  onBlur() {
    this.blurEvent.emit();
  }

  /**
   * Native HTML inputs of type `radio` only fire `onChange` when being `checked`.
   * For `ui-input type=radio` this is now also triggered when being `unchecked`.
   * See `deselectOtherRadiosInGroup` for details.
   */
  onChange() {
    if (this.isCheckboxOrRadio()) {
      this.checked = this.input.checked;
      this.changeEvent.emit({ value: this.value, checked: this.input.checked });
    }

    if (this.isRadio() && this.checked) {
      this.deselectOtherRadiosInGroup();
    }

    if (this.value !== this.input.value) {
      this.handleInputValueChange();
    }
  }

  /**
   * `onChange()` will only fire if a radio is being `checked`, NOT when it's being `unchecked`.
   * The currently checked radio-input needs to notify all other radios within its group that they
   * have been DEselected, so they can sync their `@Prop checked` to the internal `input.checked`.
   * This doesn't actually deselect the native HTML `input type=radio` - that's done automatically.
   */
  deselectOtherRadiosInGroup() {
    // collect all radios on same group (identified by 'name')
    const allRadiosInGroup = Array.from(
      window.document.querySelectorAll(
        `.ui-input[type=radio][name="${this.name}"]`
      )
    );
    // remove current radio input from array
    const otherRadiosInGroup = allRadiosInGroup.filter(
      (item) => item !== this.el
    );
    // call onDeselect() on other radio buttons to update their 'checked' prop(!)
    otherRadiosInGroup.forEach((item) => {
      (item as any as UiInput).onDeselect();
    });
  }

  /**
   * Merely acts as a hook for onChange() to be called from deselectOtherRadiosInGroup()
   */
  @Method()
  async onDeselect() {
    this.onChange();
  }

  handleInputValueChange() {
    this.value = this.input.value;

    if (this.focusValue) {
      this.autoFocusCheck(parseFloat(this.maskInstance.unmaskedValue));
    }
    this.changeEvent.emit({ value: this.value });
    if (this.value === '') {
      this.clearEvent.emit();
    }
  }

  onClearButtonClick() {
    this.value = '';
    this.changeEvent.emit({ value: this.value, changedBy: 'clearButton' });
    this.clearEvent.emit();
    this.input.focus();
  }

  onLabelClick() {
    // only allow checking a radio if unchecked, prevent unchecking if checked
    const preventRadioDeselect = this.isRadio() && this.checked;

    if (this.isCheckboxOrRadio() && !this.disabled && !preventRadioDeselect) {
      this.checked = !this.checked;
      this.onChange();
      this.webformsToggleHints();
    }
  }

  /**
   * Trigger webforms to show/hide `.hint` elements. Listeners live on the parent `form` element.
   * The `.hint` contains text that is only visible when the connected radio is checked.
   *
   * Currently for KWK form email/whatsapp toggle, quite possibly also for other forms.
   * https://integration.author.magnolia.dev.azure.union-investment.de/uip-landingpages-integrationen-site/kwkvrnw
   *
   * Only required for clicking the `label` of a radio input,
   * clicking the button/icon is sufficiently handled by webforms.
   */
  webformsToggleHints() {
    const jQ = (window as any).jQuery;
    if (jQ) {
      jQ(this.input).trigger('change', [this.input]);
    }
  }

  transformValue(str: string) {
    if (this.uppercase) {
      return str.toUpperCase();
    }
    if (this.lowercase) {
      return str.toLowerCase();
    }
    return str;
  }

  @Watch('focus')
  setFocus() {
    if (this.focus) setTimeout(() => this.input.focus(), 0);
  }

  @Watch('mask')
  @Watch('uppercase')
  @Watch('lowercase')
  applyMask() {
    const stringToRegex = (str: string) =>
      new RegExp(str.replace('/', '').slice(0, -1));
    const isRegex = (str) => {
      if (typeof str === 'string') {
        return str.startsWith('/') && str.endsWith('/');
      }
      return false;
    };
    const maskType = this.typeNumberCoveredByMask
      ? IMask.MaskedNumber
      : ((this.mask || /.*/) as any);

    if (!this.maskInstance) {
      this.maskInstance = IMask(this.input, {
        mask: isRegex(maskType)
          ? new RegExp(stringToRegex(maskType))
          : maskType,
        prepare: this.transformValue.bind(this),
        min: this.changeValue ? this.min : undefined,
        max: this.changeValue ? this.max : undefined,
        scale: this.scale,
        thousandsSeparator: this.thousandsSeparator
          ? this.thousandsSeparatorCharacter
          : '',
        radix: this.radix,
        padFractionalZeros: this.scale > 0,
        mapToRadix: ['.'],
      });
    } else {
      this.maskInstance.updateOptions({
        mask: maskType,
        prepare: this.transformValue.bind(this),
        min: this.changeValue ? this.min : undefined,
        max: this.changeValue ? this.max : undefined,
        scale: this.scale,
        thousandsSeparator: this.thousandsSeparator
          ? this.thousandsSeparatorCharacter
          : '',
        radix: this.radix,
        padFractionalZeros: this.scale > 0,
        mapToRadix: ['.'],
      });
    }
  }

  iconClicked() {
    /**
     * The following focuses the input field if the input is used as a dropdown
     * This is needed for the download-search in funds-mode
     * */
    if (this.icon === 'chevron-bottom') {
      this.input.focus();
    }
    this.iconClickEvent.emit();
  }

  changeNumberValue(addValue?: boolean) {
    let newValue = parseFloat(this.maskInstance?.unmaskedValue || this.value);

    if (addValue) {
      newValue += this.stepSize;
    } else if (!addValue) {
      newValue -= this.stepSize;
    }

    this.changeValueCheck(newValue);

    this.input.value = newValue.toString();
    if (this.maskInstance) this.maskInstance.value = this.input.value;
    this.onChange();
  }

  changeValueCheck(newValue: number) {
    this.plusDisabled = newValue >= this.max;
    this.minusDisabled = newValue <= this.min;
  }

  autoFocusCheck(value: number) {
    if (value > this.max || value < this.min) {
      this.inputError = true;
      this.error = true;
    } else {
      this.inputError = false;
      this.error = false;
    }
  }

  handleTypeNumberProps() {
    // if there are decimals allowed, we need separator
    if (this.scale > 0) {
      this.hasFractionalDelimiter = true;
    }

    // if one of values is passed - we want to use inputMask
    if (
      this.autocorrect ||
      this.hasFractionalDelimiter ||
      this.thousandsSeparator
    ) {
      this.typeNumberCoveredByMask = true;
    }

    if (this.autocorrect) {
      // default is 'change'
      this.focusValue = this.autocorrectType === 'defocus';
      this.changeValue = !this.focusValue;
    }
  }

  // return +/- stepper used for input type = number
  returnStepper() {
    return [
      <ui-icon
        key="minus-number-stepper"
        class={{
          'input__icon number-stepper': true,
          disabled: this.minusDisabled,
        }}
        icon="minus-circle"
        type="system"
        onClick={() => this.changeNumberValue(false)}
      ></ui-icon>,
      <ui-icon
        key="plus-number-stepper"
        class={{
          'input__icon number-stepper': true,
          disabled: this.plusDisabled,
        }}
        icon="plus-circle"
        type="system"
        onClick={() => this.changeNumberValue(true)}
      ></ui-icon>,
    ];
  }

  componentWillLoad() {
    if (this.type === 'number') {
      this.handleTypeNumberProps();
    }

    this.inputId = this.el.id || this.name;
  }

  componentDidLoad() {
    if (this.mask || this.thousandsSeparator || this.scale) {
      this.applyMask();
    }

    this.setFocus();
  }

  componentDidRender() {
    this.didRender.emit();
  }

  // TODO: (needs testing) always provide content via innerHTML and remove this.labelIsHtml
  getLabel() {
    const innerHtml = this.labelIsHtml ? this.label : null;
    return (
      this.label && (
        <label
          class="input__label"
          htmlFor={this.inputId}
          onClick={() => this.onLabelClick()}
          innerHTML={innerHtml}
        >
          {this.icon && this.variant === 'stylized-big' && (
            <ui-icon icon={this.icon} type="content"></ui-icon>
          )}
          {!this.labelIsHtml && this.label}
          {this.required && <span class="required">*</span>}

          {this.description && this.variant === 'stylized-big' && (
            <span class="description">{this.getDescription()}</span>
          )}
        </label>
      )
    );
  }

  getDescription() {
    return <label class="input__description">{this.description}</label>;
  }

  getInput() {
    return (
      <input
        class="input__field"
        id={this.inputId}
        type={this.typeNumberCoveredByMask ? 'text' : this.type}
        onChange={this.onChange.bind(this)}
        onKeyUp={this.isCheckboxOrRadio() ? undefined : this.onKeyUp.bind(this)}
        onFocus={this.onFocus.bind(this)}
        onBlur={this.onBlur.bind(this)}
        placeholder={this.placeholder}
        required={this.required}
        name={this.name}
        ref={(input) => {
          this.input = input as HTMLInputElement;
        }}
        disabled={this.disabled}
        value={this.value}
        checked={this.checked}
      />
    );
  }

  getInputIcons() {
    const errorIcon = (
      <ui-icon class="input__icon" icon="warning" type="system"></ui-icon>
    );

    const successIcon = (
      <ui-icon class="input__icon" icon="confirm" type="system"></ui-icon>
    );

    return (
      <span class="input__icons">
        {this.error && !this.isCheckboxOrRadio() && errorIcon}
        {this.success && !this.isCheckboxOrRadio() && successIcon}
        {this.clearable && this.value && (
          <ui-icon
            class="input__icon"
            icon="close-circle"
            type="system"
            onClick={this.onClearButtonClick.bind(this)}
          ></ui-icon>
        )}
        {this.icon && (
          <ui-icon
            class={{
              input__icon: true,
              'icon-search': true,
              'icon-active': this.value !== '',
            }}
            icon={this.icon}
            type="system"
            onClick={this.iconClicked.bind(this)}
          ></ui-icon>
        )}
        {this.type === 'number' && [
          this.unit && <p>{this.unit}</p>,
          this.stepControls && this.returnStepper(),
        ]}
      </span>
    );
  }

  getSwitchInput() {
    return (
      <label class="input__switch">
        {this.getInput()}
        <span class="input__switch__slider"></span>
      </label>
    );
  }

  render() {
    return (
      <div
        class={{
          input: true,
          'input--error': this.error,
          'input--disabled': this.disabled,
          'input--success': this.success,
          'input--checked': this.checked,
          'input--switch': this.switch,
          [`input--${this.variant}`]: !!this.variant,
          'variant-stylized': this.variant === 'stylized',
          'variant-stylized-big': this.variant === 'stylized-big',
        }}
      >
        {this.getLabel()}
        <div class="input__icon-wrapper">
          {this.switch ? this.getSwitchInput() : this.getInput()}
          {['radio', 'checkbox'].includes(this.type) && (
            <span class="input__checkmark">
              {(this.type === 'checkbox' ||
                (this.type === 'radio' && this.variant !== 'default')) &&
                !this.switch && (
                  <ui-icon type="system" icon="confirm"></ui-icon>
                )}
            </span>
          )}
          {this.getInputIcons()}
        </div>
        {this.hint && <div class="input__hint">{this.hint}</div>}
        {this.message && <div class="input__message">{this.message}</div>}
      </div>
    );
  }
}

versionComponent(UiInput);
